// Bausteine, die auf mehreren Seiten in gleicher Form auftauchen:
// Objektkarte, Einheitenzeile, Mangelkarte, Inventartabelle.

import { useState } from "react";
import { api } from "../api.ts";
import { eur, eur2, pct, datum } from "../formate.ts";
import { Mini, Leer } from "../komponenten/Bausteine.tsx";
import { useZustand } from "../zustand.tsx";
import {
  PRIO_TEXT, STATUS_TEXT, BESTAND_TEXT, ZUSTAND_TEXT, NEU_TEXT,
  type Einheit, type InventarPosition, type Mangel, type Objekt,
} from "../typen.ts";

/** „fehlt ganz oder unbrauchbar“. Zustand „mittel“ zählt bewusst nicht mit,
 *  sonst ist die halbe Bestandsaufnahme rot. */
export const istKritisch = (i: InventarPosition) =>
  i.vorhanden === "nein" || i.zustand === "schlecht";

// ------------------------------------------------------------- Objektkarte

export function ObjektKarte({ objekt }: { objekt: Objekt }) {
  const { gehe } = useZustand();
  const k = objekt.kennzahlen;
  const adresse = [objekt.strasse, [objekt.plz, objekt.ort].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");

  return (
    <div className="obj-card" onClick={() => gehe({ seite: "objekt", objektId: objekt.id })}>
      <div style={{ minWidth: 210 }}>
        <div className="obj-name">{objekt.name}</div>
        <div className="obj-addr">{adresse || "—"}</div>
        <div className="chip-row">
          {objekt.einheiten.length === 0
            ? <span className="sub" style={{ fontSize: 12 }}>Keine Einheiten</span>
            : objekt.einheiten.slice(0, 6).map((e) => (
              <span key={e.id} className={`chip ${e.status}`}>{STATUS_TEXT[e.status]}</span>
            ))}
        </div>
      </div>
      <div className="mini-kpis">
        <Mini titel="Invest" wert={eur(k.invest)} />
        <Mini titel="Kaltmiete p.a." wert={eur(k.jahreskaltmieteIst)} />
        <Mini titel="Rendite (Ist)" wert={pct(k.renditeIst)} ton="blue" />
        <Mini titel="Offene Mängel" wert={String(k.maengelOffen)}
          ton={k.maengelOffen > 0 ? "red" : undefined} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Einheitenzeile

export function EinheitZeile(
  { objekt, einheit, zeigeObjekt, aufVertrag }:
  { objekt: Objekt; einheit: Einheit; zeigeObjekt?: boolean; aufVertrag: (e: Einheit) => void },
) {
  const { gehe } = useZustand();
  const mv = einheit.mietvertrag;
  const offeneMaengel = objekt.maengel.filter((m) => m.einheit_id === einheit.id && !m.erledigt).length;
  const kritisch = einheit.inventar.filter(istKritisch).length;

  return (
    <div className="row-card">
      <div style={{ minWidth: 200 }}>
        <div className="row-title">
          <button className="linkish" style={{ fontSize: 15, fontWeight: 600 }}
            onClick={() => gehe({ seite: "einheit", objektId: objekt.id, einheitId: einheit.id })}>
            {einheit.bezeichnung}
          </button>
          <span className={`chip ${einheit.status}`}>{STATUS_TEXT[einheit.status]}</span>
          {einheit.moebliert ? <span className="chip moebel">möbliert</span> : null}
        </div>
        <div className="row-sub">
          {zeigeObjekt ? (
            <>
              <button className="linkish" onClick={() => gehe({ seite: "objekt", objektId: objekt.id })}>
                {objekt.name}
              </button>{" · "}
            </>
          ) : null}
          {einheit.typ}
          {einheit.flaeche ? ` · ${einheit.flaeche} m²` : ""}
          {mv ? ` · Mieter: ${mv.mieter_name}`
            : einheit.zielmiete ? ` · Zielmiete: ${eur2(einheit.zielmiete)}` : ""}
          {offeneMaengel > 0
            ? <> · <b style={{ color: "var(--red)" }}>{offeneMaengel} offene Mängel</b></> : null}
          {kritisch > 0 ? ` · ${kritisch} Inventar-Positionen kritisch` : ""}
        </div>
      </div>
      <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
        {mv ? (
          <>
            <Mini titel="Kaltmiete" wert={eur2(mv.kaltmiete)} ton="blue" />
            <Mini titel="Warm" wert={eur2(Number(mv.kaltmiete) + Number(mv.nebenkosten_vz))} />
            <Mini titel="seit" wert={datum(mv.beginn)} />
          </>
        ) : <span className="row-sub">Kein Mietvertrag</span>}
        <div className="row-actions">
          <button className="btn btn-quiet btn-sm"
            onClick={() => gehe({ seite: "einheit", objektId: objekt.id, einheitId: einheit.id })}>
            Öffnen
          </button>
          <button className="btn btn-quiet btn-sm" onClick={() => aufVertrag(einheit)}>
            {mv ? "Vertrag" : "+ Vertrag"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Mangelkarte

export function MangelKarte(
  { objekt, mangel, zeigeObjekt, aufBearbeiten }:
  { objekt: Objekt; mangel: Mangel; zeigeObjekt?: boolean; aufBearbeiten: (m: Mangel) => void },
) {
  const { aendern } = useZustand();
  const einheit = objekt.einheiten.find((e) => e.id === mangel.einheit_id);
  const ort = [zeigeObjekt ? objekt.name : "", einheit ? einheit.bezeichnung : "Allgemein / Haus", mangel.raum]
    .filter(Boolean).join(" · ");

  return (
    <div className={`mangel ${mangel.erledigt ? "done" : ""}`}>
      <button
        className={`cb ${mangel.erledigt ? "on" : ""}`}
        title={mangel.erledigt ? "Wieder öffnen" : "Als erledigt markieren"}
        onClick={() => void aendern(
          () => api.mangelAendern(mangel.id, { erledigt: !mangel.erledigt }),
          mangel.erledigt ? "Wieder geöffnet" : "Erledigt")}
      >
        {mangel.erledigt ? "✓" : ""}
      </button>

      <div className="mangel-body">
        <div className="mangel-title">
          <span className="txt">{mangel.beschreibung}</span>
          {mangel.erledigt
            ? <span className="chip done">erledigt {mangel.erledigt_am ? datum(mangel.erledigt_am) : ""}</span>
            : <span className={`chip ${mangel.prio}`}>{PRIO_TEXT[mangel.prio]}</span>}
          {mangel.art ? <span className="chip niedrig">{mangel.art}</span> : null}
          {mangel.investiv && mangel.kosten > 0
            ? <span className="chip moebel" title="Zählt zur Gesamtinvestition">investiv</span> : null}
        </div>
        <div className="mangel-meta">
          {ort}
          {mangel.beheber ? ` · Beheber: ${mangel.beheber}` : ""}
          {mangel.kostentraeger ? ` · Kostenträger: ${mangel.kostentraeger}` : ""}
          {mangel.faellig && !mangel.erledigt ? ` · fällig ${datum(mangel.faellig)}` : ""}
        </div>
      </div>

      <div className="mangel-right">
        <Anhaenge dateien={mangel.dateien} entitaet="mangel" entitaetId={mangel.id} />
        <Mini titel="Kosten" wert={mangel.kosten ? eur(mangel.kosten) : "–"} />
        <button className="btn btn-quiet btn-sm" onClick={() => aufBearbeiten(mangel)}>Bearbeiten</button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- Anhänge

export function Anhaenge(
  { dateien, entitaet, entitaetId }:
  { dateien: { id: string; dateiname: string; mime: string }[]; entitaet: string; entitaetId: string },
) {
  const [gross, setzeGross] = useState<string | null>(null);
  const { aendern } = useZustand();

  if (dateien.length === 0) return <div className="thumb-ph">📷</div>;

  return (
    <>
      <div className="anhang-reihe">
        {dateien.map((d) => d.mime.startsWith("image/") ? (
          <img key={d.id} className="anhang" src={api.dateiAdresse(d.id)} alt={d.dateiname}
            onClick={() => setzeGross(d.id)} />
        ) : (
          <a key={d.id} className="anhang-pdf" href={api.dateiAdresse(d.id)}
            target="_blank" rel="noreferrer" title={d.dateiname}>📄</a>
        ))}
      </div>

      {gross ? (
        <div className="lightbox" onClick={() => setzeGross(null)}>
          <img src={api.dateiAdresse(gross)} alt="" />
          <button className="btn btn-quiet btn-sm"
            style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)" }}
            onClick={(e) => {
              e.stopPropagation();
              if (!confirm("Diesen Anhang entfernen?")) return;
              void aendern(() => api.dateiLoesen(gross, entitaet, entitaetId), "Anhang entfernt");
              setzeGross(null);
            }}>
            Entfernen
          </button>
        </div>
      ) : null}
    </>
  );
}

// ------------------------------------------------------------ Mängelliste

export function MaengelListe(
  { eintraege, zeigeObjekt, aufNeu, aufBearbeiten }:
  {
    eintraege: { objekt: Objekt; mangel: Mangel }[];
    zeigeObjekt?: boolean;
    aufNeu?: () => void;
    aufBearbeiten: (objekt: Objekt, mangel: Mangel) => void;
  },
) {
  const [filter, setzeFilter] = useState<"offen" | "erledigt" | "alle">("offen");
  const gefiltert = eintraege.filter(({ mangel: m }) =>
    filter === "alle" ? true : filter === "offen" ? !m.erledigt : m.erledigt);
  const rang = { hoch: 0, mittel: 1, niedrig: 2 };
  const sortiert = [...gefiltert].sort((a, b) =>
    Number(a.mangel.erledigt) - Number(b.mangel.erledigt)
    || rang[a.mangel.prio] - rang[b.mangel.prio]);
  const summe = sortiert.reduce((s, x) => s + x.mangel.kosten, 0);

  return (
    <>
      <div className="san-head">
        <div className="seg" style={{ margin: 0 }}>
          {(["offen", "erledigt", "alle"] as const).map((f) => (
            <button key={f} className={filter === f ? "on" : ""} onClick={() => setzeFilter(f)}>
              {f === "offen" ? "Offen" : f === "erledigt" ? "Erledigt" : "Alle"}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="sub" style={{ margin: 0 }}>
            {sortiert.length} Einträge · {eur(summe)}
          </span>
          {aufNeu ? <button className="btn btn-primary btn-sm" onClick={aufNeu}>+ Mangel</button> : null}
        </div>
      </div>

      {sortiert.length === 0 ? (
        <Leer titel={filter === "erledigt" ? "Noch nichts erledigt."
          : filter === "offen" ? "Keine offenen Mängel." : "Keine Mängel erfasst."}>
          {filter !== "erledigt"
            ? "Mängel lassen sich hier oder aus einer Inventarposition heraus anlegen."
            : null}
        </Leer>
      ) : sortiert.map(({ objekt, mangel }) => (
        <MangelKarte key={mangel.id} objekt={objekt} mangel={mangel} zeigeObjekt={zeigeObjekt}
          aufBearbeiten={(m) => aufBearbeiten(objekt, m)} />
      ))}
    </>
  );
}

// -------------------------------------------------------- Inventar je Raum

export function InventarTabelle(
  { einheit, aufBearbeiten }:
  { einheit: Einheit; aufBearbeiten: (raum: string, position: InventarPosition | null) => void },
) {
  const { aendern } = useZustand();
  const raeume = [...new Set(einheit.inventar.map((i) => i.raum || "Allgemein"))];

  return (
    <>
      {raeume.map((raum) => {
        const posten = einheit.inventar.filter((i) => (i.raum || "Allgemein") === raum);
        const kosten = posten.reduce((s, i) => s + i.kosten, 0);
        const fehlt = posten.filter((i) => i.vorhanden === "nein").length;
        const schlecht = posten.filter((i) => i.zustand === "schlecht").length;

        return (
          <div className="inv-room" key={raum}>
            <div className="inv-room-hd">
              <h3>{raum}</h3>
              <span className="sub" style={{ margin: 0 }}>
                {posten.length} Positionen
                {fehlt ? ` · ${fehlt} fehlen` : ""}
                {schlecht ? ` · ${schlecht} schlecht` : ""}
                {kosten ? ` · Neu: ${eur(kosten)}` : ""}
              </span>
            </div>
            <div className="inv">
              <div className="irow head">
                <span>Position</span><span className="h-m">Bedarf</span><span>Bestand</span>
                <span className="h-m">Anmerkung</span><span className="h-m">Neu?</span>
                <span className="num">Kosten</span><span />
              </div>
              {posten.map((i) => (
                <div className="irow" key={i.id}>
                  <div>
                    <b style={{ fontWeight: 600 }}>{i.bezeichnung}</b>
                    {i.pflicht === "optional" ? <span className="opt-tag">optional</span> : null}
                    <div className={`zust ${i.zustand || "leer"}`}
                      style={{ marginTop: 4, maxWidth: 92 }}>{ZUSTAND_TEXT[i.zustand]}</div>
                  </div>
                  <span className="h-m sub" style={{ margin: 0, fontSize: 13 }}>{i.pflicht}</span>
                  <span className={`vorh ${i.vorhanden === "teilweise" ? "teil" : i.vorhanden}`}>
                    {BESTAND_TEXT[i.vorhanden]}
                  </span>
                  <span className="h-m sub" style={{ margin: 0, fontSize: 13 }}>{i.anmerkung}</span>
                  <span className="h-m sub" style={{ margin: 0, fontSize: 13 }}>{NEU_TEXT[i.neu] ?? "—"}</span>
                  <span className="num">{i.kosten ? eur(i.kosten) : "–"}</span>
                  <span style={{ display: "flex", gap: 6 }}>
                    {istKritisch(i) || i.zustand === "mittel" ? (
                      <button className="btn-xs" title="Mangel aus dieser Position erzeugen"
                        onClick={() => void aendern(
                          () => api.mangelAusInventar(i.id), "Mangel angelegt")}>
                        → Mangel
                      </button>
                    ) : null}
                    <button className="btn-xs" onClick={() => aufBearbeiten(raum, i)}>Bearb.</button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}
